import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { CognitoUser } from "@aws-amplify/auth";

import { QueryStatus } from "../../app/types";
import {
  ValidateBulkUploadInvitation,
  CreateUserInvitationInput,
  EULAText,
  Institution,
  Program,
  ProgramRole,
  SearchUserInvitationsQueryVariables,
  SearchableIDFilterInput,
  SearchableSortDirection,
  SearchableStringFilterInput,
  SearchableUserInvitationFilterInput,
  SearchableUserInvitationSortInput,
  SearchableUserInvitationSortableFields,
  User,
  UserInvitation,
  ValidateBulkUploadInvitationResult,
  TraineeType,
  CreateUserInput,
  InvitationStatus,
  CreateProgramMembershipInput,
  ProgramMembership
} from "../../API";
import {
  createCognitoAccount,
  createUserInvitation as createUserInvitationAPI,
  fetchUserInvitation,
  confirmCognitoAccount,
  resendConfirmationCode as resendConfirmationCodeAPI,
  fetchAllUserInvitations,
  fetchEulaText,
  searchUserInvitations,
  fetchUserInvitationsByProgramIds,
  fetchUserInvitationByEmail,
  fetchValidateBulkUpload
} from "./userInvitationsAPI";
import { returnUsersAdminedProgramMemberships } from "../Users/userUtils";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid";
import { cloneDeep, isEqual } from "lodash";
import { sendUserInvitation as sendUserInvitationAPI, createUser as createUserAPI, type ResendInvitationResponse } from "../Users/usersAPI";
import { BulkInviteErrors } from "../../lib/helpers-lib/shared-constants";
import { API } from "aws-amplify";

export type ValidateBulkUploadError = {
  field: string;
  error: string;
};

export type BulkInvitation = {
  requestId?: string;
  firstName: string;
  lastName: string;
  email: string;
  npi?: number;
  pgy?: number;
  role: ProgramRole;
  traineeType?: string;
  programIdFromFile?: string;
  programId?: string;
  institutionId?: string;
  simplLearnId?: string;
  jobTitle?: string;
  errors?: {
    field: string;
    error: string;
  }[];
  statusMessage?: string;
};

export const validateBulkUploadInvitationResultToBulkInvitation = (
  i: ValidateBulkUploadInvitationResult
): BulkInvitation => ({
  ...i,
  requestId: i.requestId ?? undefined,
  pgy: i.pgy ? parseInt(i.pgy) : undefined,
  npi: i.npi ? parseInt(i.npi) : undefined,
  traineeType: i.traineeType ?? undefined,
  simplLearnId: i.simplLearnId ?? undefined,
  jobTitle: i.jobTitle ?? undefined,
  errors: i.errors
    ? i.errors.map((e) => ({ field: e.field, error: e.error }))
    : undefined
});

export const bulkInvitationToValidateBulkUploadInvitation = (
  i: BulkInvitation
): ValidateBulkUploadInvitation => ({
  requestId: i.requestId,
  email: i.email,
  firstName: i.firstName,
  lastName: i.lastName,
  role: i.role,
  traineeType: i.traineeType ? i.traineeType : undefined,
  programId: i.programId ?? "",
  npi: i.npi?.toString() ?? "",
  simplLearnId: i.simplLearnId ?? "",
  pgy: i.pgy?.toString() ?? "",
  jobTitle: i.jobTitle ?? ""
});

export const Permission = {
  Users: "Users",
  ProgramAdmins: "ProgramAdmins",
  InstitutionalAdmins: "InstitutionalAdmins"
};

export const PermissionSelectFields = [
  { label: "Users", value: Permission.Users },
  { label: "Program Administrators", value: Permission.ProgramAdmins },
  {
    label: "Institutional Administrators",
    value: Permission.InstitutionalAdmins
  }
];

export const useUserInvitationsByProgramIds = (programIds?: string[]) => {
  const [userInvitations, setUserInvitations] = useState<
    UserInvitation[] | null
  >(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getUserInvitationsByProgramIds = async (
    programIds: string[]
  ): Promise<UserInvitation[] | null> => {
    setStatus("loading");
    setError(null);

    try {
      const usersInvitationsForProgram = await fetchUserInvitationsByProgramIds(
        programIds
      );
      setUserInvitations(usersInvitationsForProgram);
      setStatus("succeeded");
      return usersInvitationsForProgram;
    } catch (error) {
      console.error("error in useUserInvitationsByProgramId:", error);
      const errorString = `error in useUserInvitationsByProgramId: ${error}`;
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  useEffect(() => {
    if (programIds) {
      getUserInvitationsByProgramIds(programIds);
    }
  }, [JSON.stringify(programIds)]);

  return {
    userInvitations,
    status,
    error,
    getUserInvitationsByProgramId: getUserInvitationsByProgramIds
  };
};

export const useUserInvitationById = (id?: string, unauthed?: boolean) => {
  const [userInvitation, setUserInvitation] = useState<UserInvitation | null>(
    null
  );
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getUserInvitationById = async (id: string, unauthed?: boolean) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchUserInvitation(id, unauthed);
      setUserInvitation(result);
      setStatus("succeeded");
    } catch (error) {
      console.error("error fetching user invitation:", error);
      const errorString = JSON.stringify(error);
      setStatus("failed");
      setError(errorString);
    }
  };

  useEffect(() => {
    if (id) {
      getUserInvitationById(id, unauthed);
    }
  }, [id, unauthed]);

  return {
    userInvitation,
    status,
    error,
    getUserInvitationById
  };
};

export const useUserInvitationByEmail = (
  email?: string,
  unauthed?: boolean
) => {
  const [userInvitation, setUserInvitation] = useState<UserInvitation | null>(
    null
  );
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getUserInvitationByEmail = async (
    email: string,
    unauthed?: boolean
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchUserInvitationByEmail(email, unauthed);
      setUserInvitation(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      console.error("error fetching user invitation:", error);
      const errorString = JSON.stringify(error);
      setStatus("failed");
      setError(errorString);
      return null;
    }
  };

  useEffect(() => {
    if (email) {
      getUserInvitationByEmail(email, unauthed);
    }
  }, [email, unauthed]);

  return {
    userInvitation,
    status,
    error,
    getUserInvitationByEmail
  };
};

export const useEula = (enabled: boolean = true) => {
  const [eula, setEula] = useState<EULAText | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getEulaText = async () => {
    if (!enabled) return;
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchEulaText();
      setEula(result);
      setStatus("succeeded");
    } catch (error) {
      console.error("error fetching EULAText:", error);
      // Extract a meaningful error message
      let errorMessage = "Unable to load EULA text.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = String(error.message);
      }
      setStatus("failed");
      setError(errorMessage);
    }
  };
  useEffect(() => {
    if (enabled) {
      getEulaText();
    } else {
      // Reset state when disabled
      setStatus("idle");
      setError(null);
    }
  }, [enabled]);
  return {
    eula,
    status,
    error,
    getEulaText
  };
};

export const useValidateBulkUpload = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [returnInvitations, setReturnInvitations] = useState<
    BulkInvitation[] | null | undefined
  >();
  const [requestedInvitations, setRequestedInvitations] = useState<
    BulkInvitation[] | null | undefined
  >();

  const validateBulkUpload = async (invitations: BulkInvitation[]) => {
    setStatus("loading");
    setRequestedInvitations(invitations);
    try {
      const invitationsToValidate: ValidateBulkUploadInvitation[] = [];
      // CHANGE: Start with an empty array instead of cloning returnInvitations
      const validatedInvitations: BulkInvitation[] = [];

      // Check for duplicates within the file (case-insensitive for emails)
      const duplicateErrors = checkForDuplicates(invitations);

      invitations.forEach((i) => {
        // Start with duplicate errors if this invitation has duplicates
        // Duplicate errors must always be recalculated because they depend on ALL invitations
        const requestId = i.requestId || "";
        const duplicateErrorsForInvitation = duplicateErrors.get(requestId) || [];
        const foundInvitation = returnInvitations?.find(
          (ri) => ri.requestId == i.requestId
        );
        
        // Check if this invitation's fields have changed
        const invitationChanged =
          !foundInvitation ||
          foundInvitation.email != i.email ||
          foundInvitation.firstName != i.firstName ||
          foundInvitation.lastName != i.lastName ||
          foundInvitation.npi != i.npi ||
          foundInvitation.pgy != i.pgy ||
          foundInvitation.programId != i.programId ||
          foundInvitation.role != i.role ||
          foundInvitation.traineeType != i.traineeType ||
          foundInvitation.jobTitle != i.jobTitle;
        
        if (invitationChanged) {
          // Invitation fields changed - need to re-validate
          const errors = [...duplicateErrorsForInvitation, ...validateInvitation(i)];
          if (errors.length === 0) {
            // Valid invitation, add to invitationsToValidate
            invitationsToValidate.push(
              bulkInvitationToValidateBulkUploadInvitation(i)
            );
            validatedInvitations.push(i);
          } else {
            // Invalid invitation, add to validatedInvitations with errors
            validatedInvitations.push({
              ...i,
              errors: errors
            });
          }
        } else {
          // Invitation fields haven't changed, but duplicate errors may have changed
          // because duplicates depend on ALL invitations in the file
          // Merge current duplicate errors with existing non-duplicate errors from foundInvitation
          const existingNonDuplicateErrors = (foundInvitation.errors || []).filter(
            (error) => !error.error.toLowerCase().includes("duplicate")
          );
          const allErrors = [...duplicateErrorsForInvitation, ...existingNonDuplicateErrors];
          
          if (allErrors.length === 0) {
            // No errors, use the existing invitation but ensure it has no errors
            validatedInvitations.push({
              ...foundInvitation,
              errors: undefined
            });
          } else {
            // Has errors (duplicate or other), update the existing invitation with current errors
            validatedInvitations.push({
              ...foundInvitation,
              errors: allErrors
            });
          }
        }
      });

      if (invitationsToValidate.length > 0) {
        const result = await fetchValidateBulkUpload(invitationsToValidate);

        result?.invitations?.forEach((i) => {
          const index = validatedInvitations.findIndex(
            (vi) => vi.requestId == i.requestId
          );
          const validatedInvitation = validateBulkUploadInvitationResultToBulkInvitation(i as ValidateBulkUploadInvitationResult);
          if (index !== -1) {
            // Merge backend validation errors with current duplicate errors
            // Duplicate errors are always calculated from current invitations, not from backend
            const requestId = validatedInvitation.requestId || "";
            const duplicateErrorsForInvitation = duplicateErrors.get(requestId) || [];
            const backendErrors = validatedInvitation.errors || [];
            
            // Combine duplicate errors (from frontend check) with backend validation errors
            const allErrors = [...duplicateErrorsForInvitation, ...backendErrors];
            
            // Update in place to maintain order, with merged errors
            validatedInvitations[index] = {
              ...validatedInvitation,
              errors: allErrors.length > 0 ? allErrors : undefined
            };
          }
        });
      }

      // if the user has removed any invitations, remove them from the validation list
      const filteredValidatedInvitations = validatedInvitations.filter(
        (vi) => !!invitations.find((i) => i.requestId == vi.requestId)
      );

      setStatus("succeeded");
      const finalInvitations =
        requestedInvitations && requestedInvitations.length > 0
          ? filteredValidatedInvitations.filter((invitation) =>
              requestedInvitations.some(
                (req) => req.requestId === invitation.requestId
              )
            )
          : filteredValidatedInvitations;
      setReturnInvitations(finalInvitations);
    } catch (error) {
      console.error("error validating bulk upload:", error);
      setStatus("failed");
    }
  };

  /**
   * Check for duplicate emails and NPIs within the invitations array
   * Returns a Map of requestId -> array of duplicate errors
   */
  const checkForDuplicates = (
    invitations: BulkInvitation[]
  ): Map<string, ValidateBulkUploadError[]> => {
    const duplicateErrors = new Map<string, ValidateBulkUploadError[]>();
    
    // Track emails (case-insensitive) and NPIs
    const emailMap = new Map<string, string[]>(); // lowercase email -> array of requestIds
    const npiMap = new Map<string, string[]>(); // npi (as string) -> array of requestIds

    // First pass: collect all emails and NPIs
    invitations.forEach((invitation) => {
      const requestId = invitation.requestId || "";
      
      // Check email (case-insensitive)
      if (invitation.email) {
        const emailLower = invitation.email.toLowerCase().trim();
        if (emailLower) {
          const existing = emailMap.get(emailLower) || [];
          existing.push(requestId);
          emailMap.set(emailLower, existing);
        }
      }

      // Check NPI (if provided) - BulkInvitation.npi is number | undefined
      if (invitation.npi !== undefined && invitation.npi !== null) {
        const npiValue = String(invitation.npi);
        if (npiValue && npiValue !== "") {
          const existing = npiMap.get(npiValue) || [];
          existing.push(requestId);
          npiMap.set(npiValue, existing);
        }
      }
    });

    // Second pass: add errors for duplicates
    invitations.forEach((invitation) => {
      const requestId = invitation.requestId || "";
      const errors: ValidateBulkUploadError[] = [];

      // Check if email is duplicated
      if (invitation.email) {
        const emailLower = invitation.email.toLowerCase().trim();
        const emailRequestIds = emailMap.get(emailLower);
        if (emailRequestIds && emailRequestIds.length > 1) {
          errors.push({
            field: "email",
            error: `Duplicate email found in file. This email appears ${emailRequestIds.length} time(s).`
          });
        }
      }

      // Check if NPI is duplicated - BulkInvitation.npi is number | undefined
      if (invitation.npi !== undefined && invitation.npi !== null) {
        const npiValue = String(invitation.npi);
        if (npiValue && npiValue !== "") {
          const npiRequestIds = npiMap.get(npiValue);
          if (npiRequestIds && npiRequestIds.length > 1) {
            errors.push({
              field: "npi",
              error: `Duplicate NPI found in file. This NPI appears ${npiRequestIds.length} time(s).`
            });
          }
        }
      }

      if (errors.length > 0) {
        duplicateErrors.set(requestId, errors);
      }
    });

    return duplicateErrors;
  };

  const validateInvitation = (
    invitation: BulkInvitation
  ): ValidateBulkUploadError[] => {
    const errors: ValidateBulkUploadError[] = [];

    // Check if role is a valid ProgramRole
    const validRoles = Object.values(ProgramRole);
    if (!validRoles.includes(invitation.role as ProgramRole)) {
      errors.push({
        field: "role",
        error:
          "Role is invalid. It must be one of Trainee, Attending, ProgramAdmin, or Admin."
      });
    }

    if (typeof invitation.email !== "string" || !invitation.email) {
      errors.push({
        field: "email",
        error: "Email is required and must be a string."
      });
    }

    if (typeof invitation.firstName !== "string" || !invitation.firstName) {
      errors.push({
        field: "firstName",
        error: "First name is required and must be a string."
      });
    }

    if (typeof invitation.lastName !== "string" || !invitation.lastName) {
      errors.push({
        field: "lastName",
        error: "Last name is required and must be a string."
      });
    }

    if (
      invitation.npi !== undefined &&
      invitation.npi !== null &&
      typeof invitation.npi !== "string" &&
      typeof invitation.npi !== "number"
    ) {
      errors.push({
        field: "npi",
        error: "NPI must be a string if provided."
      });
    }

    if (
      invitation.simplLearnId !== undefined &&
      invitation.simplLearnId !== null &&
      typeof invitation.simplLearnId !== "string"
    ) {
      errors.push({
        field: "simplLearnId",
        error: "SimplLearn ID must be a string if provided."
      });
    }

    if (invitation.pgy !== undefined && invitation.pgy !== null) {
      if (
        typeof invitation.pgy !== "string" &&
        typeof invitation.pgy !== "number"
      ) {
        errors.push({
          field: "pgy",
          error: "PGY must be a string or number if provided."
        });
      } else if (!isValidPGY(invitation.pgy, invitation.role)) {
        errors.push({
          field: "pgy",
          error: "PGY is invalid here."
        });
      }
    }

    // Check if traineeType is either a valid ProgramRole or is null/undefined
    if (
      invitation.traineeType !== undefined &&
      invitation.traineeType !== null &&
      typeof invitation.traineeType !== "string"
    ) {
      errors.push({
        field: "traineeType",
        error: "Trainee Type must be a string if provided."
      });
    }

    return errors;
  };

  // Utility function to check if PGY is valid
  const isValidPGY = (
    pgy: string | number | null,
    role: ProgramRole
  ): boolean => {
    if (role === ProgramRole.Trainee) {
      if (pgy === null || pgy === undefined || pgy === "") return false;
      const pgyNum = typeof pgy === "string" ? parseInt(pgy, 10) : pgy;
      return Number.isInteger(pgyNum) && pgyNum >= 1 && pgyNum <= 12;
    } else {
      return pgy === null || pgy === undefined || pgy === "";
    }
  };

  return {
    status,
    invitations: returnInvitations,
    validateBulkUpload
  };
};

export interface SubmitInvitationResponseInput {
  email: string;
  password: string;
  role: string;
}

export const useSubmitInvitationResponse = () => {
  const [newCognitoUser, setNewCognitoUser] = useState<CognitoUser | null>(
    null
  );
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const submitInvitationResponse = async ({
    email,
    password,
    role
  }: SubmitInvitationResponseInput) => {
    setStatus("loading");
    setError(null);

    let permissions = "";
    // map permission level based on role
    switch (role) {
      case ProgramRole.Admin: {
        permissions = Permission.InstitutionalAdmins;
        break;
      }
      case ProgramRole.ProgramAdmin: {
        permissions = Permission.ProgramAdmins;
        break;
      }
      case ProgramRole.Trainee:
      case ProgramRole.Attending:
      default: {
        permissions = Permission.Users;
        break;
      }
    }

    try {
      const newCognitoUser = await createCognitoAccount({
        email,
        password,
        permissions
      });
      setNewCognitoUser(newCognitoUser);
      setStatus("succeeded");
      return newCognitoUser;
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
  };
  return {
    newCognitoUser,
    status,
    error,
    submitInvitationResponse
  };
};

export const useSubmitConfirmationCode = () => {
  const [accountConfirmed, setAccountConfirmed] = useState(false);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const submitConfirmationCode = async (email: string, code: string) => {
    setStatus("loading");
    setError(null);

    try {
      const confirmationResult = await confirmCognitoAccount({
        username: email,
        code
      });
      setAccountConfirmed(true);
      setStatus("succeeded");
      return confirmationResult;
    } catch (error) {
      const errorString = JSON.stringify(error);
      const anyError = error as any;
      console.error(errorString);
      setStatus("failed");
      // TODO: When the backend is in place for confirmation emails and the like,
      // we'll need to revisit this and test the actual error objects.
      // ie: this is placeholder for testing
      if (anyError.name === "CodeMismatchException") {
        setError(InvitationFormErrors.IncorrectConfirmationCode);
      } else {
        setError(InvitationNetworkErrors.ConfirmCognitoAccountError);
      }
      return error;
    }
  };

  return {
    accountConfirmed,
    status,
    error,
    submitConfirmationCode
  };
};

export const useResendConfirmationCode = () => {
  const [results, setResults] = useState<string | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const resendConfirmationCode = async (username: string) => {
    setStatus("loading");
    setError(null);

    try {
      const resendResults = await resendConfirmationCodeAPI(username);
      setResults(`resend results: ${resendResults}`);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setError(errorString);
    }
  };

  return {
    results,
    status,
    error,
    resendConfirmationCode
  };
};

export const useBulkResendUserInvitations = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [successCount, setSuccessCount] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const bulkResendUserInvitations = async (userIds: string[]) => {
    if (!userIds || userIds.length === 0) {
      setStatus("failed");
      return;
    }

    setStatus("loading");
    setSuccessCount(0);
    setCurrentStep(0);

    try {
      const path = "invites/resend";
      const rawResponse = await API.post("simpl-api", path, {
        body: { userIds }
      });
      
      const response = rawResponse as ResendInvitationResponse;

      if ("error" in response) {
        throw new Error(response.error);
      }

      // Read successCount from response (lambda returns { success: true, successCount: number, ... })
      const count = response.successCount ?? 0;
      
      if (count === 0 && response.results && response.results.length > 0) {
        // Fallback: count successful results if successCount is missing
        const actualCount = response.results.filter(r => r.success).length;
        setSuccessCount(actualCount);
      } else {
        setSuccessCount(count);
      }
      setCurrentStep(userIds.length);
      setStatus("succeeded");
    } catch (error) {
      console.error("Error resending invitations:", error);
      setStatus("failed");
    }
  };

  return {
    status,
    successCount,
    currentStep,
    bulkResendUserInvitations
  };
};

export const useSendUserInvitation = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const sendUserInvitation = async (userId: string) => {
    setStatus("loading");
    setError(null);

    try {
      await sendUserInvitationAPI(userId);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
  };

  return {
    status,
    error,
    sendUserInvitation
  };
};

export const useCreateUserInvitation = () => {
  const [newUserInvitation, setNewuserInvitation] =
    useState<UserInvitation | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const createUserInvitation = async (input: CreateUserInvitationInput) => {
    setStatus("loading");
    setError(null);

    try {
      // Convert the email to lowercase before creating the invitation.
      const lowercaseEmail = input.email?.toLowerCase();
      input.emailLower = lowercaseEmail;
      const creationResult = await createUserInvitationAPI(input);
      setNewuserInvitation(creationResult);
      setStatus("succeeded");
      return creationResult;
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    newUserInvitation,
    status,
    error,
    createUserInvitation
  };
};

export const useAllUserInvitations = () => {
  const [allUserInvitations, setAllUserInvitations] = useState<
    UserInvitation[] | null
  >(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getAllUserInvitations = async () => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchAllUserInvitations();
      setAllUserInvitations(results);
      setStatus("succeeded");
      return results;
    } catch (error) {
      const errorString = `${error}`;
      console.error("useAllUserInvitations error:", error);
      setStatus("failed");
      setError(errorString);
    }
  };

  useEffect(() => {
    getAllUserInvitations();
  }, []);

  return {
    allUserInvitations,
    status,
    error,
    getAllUserInvitations
  };
};

export const setBulkInviteStatusMessage = (
  statusMessages: Map<string, BulkInviteInvitationStatus | string>,
  message: BulkInviteInvitationStatus | string,
  setStatusMessages: Dispatch<
    SetStateAction<Map<string, BulkInviteInvitationStatus | string>>
  >,
  requestId?: string
) => {
  if (requestId) {
    // Create a new Map instance to ensure React detects the state change
    const updatedStatusMessages = new Map(statusMessages);
    updatedStatusMessages.set(requestId, message);
    setStatusMessages(updatedStatusMessages);
  }
};

interface FilterUserInvitationsByProgramAdminProps {
  invitations: UserInvitation[];
  currentUser: User;
}

export const filterUserInvitationsByProgramAdmin = ({
  invitations,
  currentUser
}: FilterUserInvitationsByProgramAdminProps): UserInvitation[] => {
  // Get the list of programs that the current user is an admin of.
  const usersAdminedProgramMemberships =
    returnUsersAdminedProgramMemberships(currentUser);
  const adminedProgramIds = usersAdminedProgramMemberships.map(
    (programMembership) => programMembership.programId
  );
  const invitationsFilteredToProgramAdminsMemberships =
    invitations.filter((userInvitation) =>
      adminedProgramIds.includes(userInvitation.programId || "")
    ) || [];
  return invitationsFilteredToProgramAdminsMemberships;
};

export const InvitationFormErrors = {
  NoInstitutionForCurrentUser:
    "You have no associated institutions. Please contact your administrator",
  NoCurrentuser:
    "There is no currentUser selected (you). Please select a user to act as or contact your administrator.",
  NoProgramForCurrentUser:
    "You have no associated programs. Please contact your administrator",
  NoPassword: "Please enter a value for the password.",
  NoConfirmPassword: "Please enter a value for the confirmation password.",
  PasswordsDoNotMatch: "Please make sure that the passwords match.",
  NoConfirmationCode: "Please enter a value for the confirmation code.",
  IncorrectConfirmationCode:
    "Confirmation code is incorrect. Please check your entry and try again.",
  PasswordRequirementsNotMet:
    "Passwords must be 8 characters in length and contain a combination of: uppercase and lowercase letters, numbers, and special characters",
  NPIRequiredError: "Please enter a value for the NPI.",
  NPIOutOfRangeError:
    "The NPI must be a ten-digit number between 1000000000 and 2999999999.",
  InstitutionRequiredError: "Please select an institution.",
  ProgramRequiredError: "Please select a program.",
  RoleRequiredError: "Please select a role.",
  PGYRequiredError: "Please select a PGY.",
  TraineeTypeRequiredError: "Please select a trainee type.",
  EmailInUseError:
    "A user already exists on the system with this email address. Use another email address or contact your administrator.",
  NpiInUseError:
    "A user already exists on the system with this NPI. Use another NPI or contact your administrator.",
  EmailNotFound:
    "There is no invitation associated with this email address. Please contact your program administrator for assistance."
};

export const InvitationNetworkErrors = {
  CreateInvitationError:
    "There was an error creating the invitation. Please try again or contact your administrator.",
  CreateCognitoAccountError:
    "There was an error creating the Cognito Account for this user. Please try again or contact your administrator.",
  ConfirmCognitoAccountError:
    "There was an error confirming the Cognito Account for this user. Please try again or contact your administrator",
  FetchAllInvitationsError:
    "Error fetching invitations. Please try again or contact your administrator.",
  FetchInvitationError:
    "Error fetching invitation. Please try again or contact your administrator.",
  InvitationNotFound: "Invitation not found!",
  InvitationAlreadyResolved:
    "This invitation is no longer available as it has already been resolved. Please contact your administrator for further assistance.",
  UserByEmailError: "Unable to check if this email exists in the system."
};

export const InvitationNotifications = {
  NoInvitations: "No invitations to display.",
  InvitationNotFound: "Invitation not found!",
  PasswordInstructions:
    "Create a password for the account. Passwords must be a minimum of 8 characters and consist of a combination of: uppercase and lowercase letters, numbers, and special characters.",
  ConfirmationSuccess: "Account successfully confirmed! Please sign in."
};

export enum BulkInviteInvitationStatus {
  NotSubmitted = "Not Submitted",
  Sent = "Successfully invited.",
  Error = "Error",
  NameRequired = "Name is required.",
  RoleRequired = "Role is required.",
  RoleOutOfRange = "Role is out of range.",
  EmailRequired = "Email is required.",
  ImproperEmail = "Email is improperly formatted.",
  EmailInUse = "Email is in use.",
  NPIRequired = "An NPI is required.",
  NPINaN = "NPI is not a number.",
  NPIOutOfRange = "NPI out of range.",
  NPIInUse = "NPI is in use.",
  PGYRequired = "A PGY is required.",
  PGYOutOfRange = "PGY out of range.",
  PGYNaN = "PGY is not a number.",
  NoProgram = "Program doesn't exist.",
  InviteError = "Error Inviting user.",
  NetworkError = "Network Error.",
  ReadyToInvite = "Ready to invite",
  MultipleErrors = "Multiple errors present. Click edit to view.",
  AwaitingRevalidation = "Awaiting revalidation",
  ExistingUser = "Existing User. Edit to add to program."
}

export const useOpenSearchUserInvitations = (
  limit: number,
  sortModel: GridSortModel,
  filterModel: GridFilterModel,
  institutions: Institution[],
  programs: Program[]
) => {
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);

  // The token that was used to get the current page. Starts undefined.
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(
    undefined
  );

  // The next token that came back with the current result. Used to get next page.
  const [nextToken, setNextToken] = useState("");

  // A stack of tokens to pop off of when going back. When it reaches the 'undefined' entry, it's back at the beginning.
  const [prevNextTokens, setPrevNextTokens] = useState<(string | undefined)[]>(
    []
  );
  const [sort, setSort] = useState<SearchableUserInvitationSortInput[]>([
    {
      direction: SearchableSortDirection.asc,
      field: SearchableUserInvitationSortableFields.email
    }
  ]);

  const [filter, setFilter] = useState<SearchableUserInvitationFilterInput>({});

  /**
   * Grab a batch of Users from the backend using sort, filter, limit, and page tokens.
   * @param getPageToken - Token to use to fetch the desired page.
   */
  const getBatch = async (getPageToken?: string) => {
    setStatus("loading");
    setError("");

    try {
      const predicate: SearchUserInvitationsQueryVariables = {
        sort: sort,
        filter: filter,
        limit: limit,
        nextToken: getPageToken
      };

      const results = await searchUserInvitations(predicate);

      // Filter out any null items from the array
      // (this likely won't happen, but TS thinks it might)
      const returnedUserInvitations = results?.items.flatMap((item) =>
        item ? [item] : []
      );

      // If the list has items, set it.
      if (returnedUserInvitations) {
        setInvitations(returnedUserInvitations);
      }

      // Utilize the OpenSearch count/total results.
      const totalResults = results?.total;
      if (totalResults) {
        setTotal(totalResults);
      }

      // If there's a nextToken included in the results of this batch,
      // set it in the state.
      const returnedNextToken = results?.nextToken;
      if (returnedNextToken) {
        setNextToken(returnedNextToken);
      }
      setStatus("succeeded");
    } catch (error) {
      console.error("error searching users:", error);
      const errorString = `error searching users: ${error}`;
      setStatus("failed");
      setError(errorString);
    }
  };

  const nextPage = () => {
    // Add the token that was used to fetch this page to the stack.
    // The first one will be empty so that going back to page 1 will
    // do the initial fetch.
    const newPrevNextTokens = [...prevNextTokens, currentPageToken];

    // Set the state
    setPrevNextTokens(newPrevNextTokens);
    setCurrentPageToken(nextToken);

    // Fetch from server
    getBatch(nextToken);
  };

  const prevPage = () => {
    // Make a non-modifying clone of the state.
    const clonedPrevTokens = [...prevNextTokens];

    // Grab the last entry/modify the cloned array.
    const prevToken = clonedPrevTokens.pop();

    // Set state
    setPrevNextTokens(clonedPrevTokens);
    setCurrentPageToken(prevToken);

    // Fetch from server
    getBatch(prevToken);
  };

  const handleSortChange = (newSortModel: GridSortModel) => {
    // Default sorting to "firstName" and "ascending"
    let field: SearchableUserInvitationSortableFields =
      SearchableUserInvitationSortableFields.email;
    let direction: SearchableSortDirection = SearchableSortDirection.asc;

    // Get the first entry in the sort model passed in from DataGrid
    // (multiple sort fields are only supported with DataGrid pro)
    const sortObject = newSortModel[0];

    if (sortObject) {
      if (sortObject.sort === "desc") {
        direction = SearchableSortDirection.desc;
      }
      switch (sortObject.field) {
        case "email":
          field = SearchableUserInvitationSortableFields.email;
          break;
        default:
          field = SearchableUserInvitationSortableFields.email;
      }
    }
    setSort([
      {
        direction: direction,
        field: field
      }
    ]);
  };

  const handleFilterChange = (newFilterModel: GridFilterModel) => {
    // Initiate an empty filter object.
    const backendFilter: SearchableUserInvitationFilterInput = {};

    // Get the first entry in the filter model passed in from DataGrid
    // (multiple filter fields are only supported with DataGrid pro)
    const filterObject = newFilterModel.items[0];

    if (filterObject && filterObject.value && filterObject.field) {
      // Use wildcard; mapping converts to contains and strips '*'
      const filterValue: SearchableStringFilterInput = {
        wildcard: `*${String(filterObject.value)}*`
      };
      switch (filterObject.field) {
        // Point the filter to whatever field was modified in DataGrid
        case "email":
          backendFilter.email = filterValue;
          break;
        case "invitationStatus":
          backendFilter.invitationStatus = filterValue;
          break;
        case "role":
          backendFilter.role = filterValue;
          break;
        case "institutionName":
          // Send name via wildcard; mapping converts to contains
          (backendFilter as any).institution = filterValue;
          break;
        case "programName": {
          // Send name via wildcard; mapping converts to contains
          (backendFilter as any).program = filterValue;
        }
      }
    }
    setFilter(backendFilter);
  };

  // If the limit is changed for the DataGrid component, get the first
  // batch again with the new limit in place.
  useEffect(() => {
    getBatch();
  }, [limit]);

  // If the sortModel changes from DataGrid, perform the logic that
  // sets the sort state.
  useEffect(() => {
    handleSortChange(sortModel);
  }, [sortModel]);

  // After the sort state has updated, get the first batch.
  // (note: this was needed because trying to call getBatch immediately
  // after calling setState was using stale sort info.)
  useEffect(() => {
    getBatch();
  }, [sort]);

  // If the filterModel changes from DataGrid, perform the logic that
  // sets the filter state.
  useEffect(() => {
    handleFilterChange(filterModel);
  }, [filterModel]);

  // After the filter state has updated, get the first batch.
  // (note: this was needed because trying to call getBatch immediately
  // after calling setState was using stale filter info.)
  useEffect(() => {
    getBatch();
  }, [filter]);

  return {
    invitations,
    status,
    error,
    total,
    nextToken,
    nextPage,
    prevPage
  };
};

type ProcessInvitationsProps = {
  validatedInvitations: BulkInvitation[] | null | undefined;
  statusMessages: Map<string, BulkInviteInvitationStatus | string>;
  selectedProgramId: string;
  currentUsersProgramId?: string;
  currentUser?: User;
  setStatusMessages: Dispatch<SetStateAction<Map<string, BulkInviteInvitationStatus | string>>>;
  setIsProcessing?: (isProcessing: boolean) => void;
};

export const processInvitations = async ({
  validatedInvitations,
  statusMessages,
  selectedProgramId,
  currentUsersProgramId,
  currentUser,
  setStatusMessages,
  setIsProcessing
}: ProcessInvitationsProps) => {
  // Set loading state to block UI
  if (setIsProcessing) {
    setIsProcessing(true);
  }

  try {
    if (validatedInvitations && validatedInvitations.length > 0) {
      // Build up status updates in a local Map, then apply all at once
      const updatedStatusMessages = new Map(statusMessages);
      
      for (const invitation of validatedInvitations) {
      // Jump out immediately if this invitation was already succesfully sent.
      if (invitation.requestId) {
        const statusMessage = updatedStatusMessages.get(invitation.requestId);
        if (statusMessage === BulkInviteInvitationStatus.Sent) continue;
        
        // Skip invitations that are awaiting revalidation
        if (statusMessage === BulkInviteInvitationStatus.AwaitingRevalidation) {
          console.log("Skipping invitation awaiting revalidation:", invitation.requestId);
          continue;
        }
      }

      // Skip invitations that still have errors
      if (invitation.errors && invitation.errors.length > 0) {
        console.log("Skipping invitation with errors:", {
          requestId: invitation.requestId,
          errors: invitation.errors
        });
        continue;
      }

      try {
        const programId =
          invitation.programId ??
          selectedProgramId ??
          currentUsersProgramId ??
          "";

        if (!currentUser?.id) {
          console.error("Current user ID is required to create invitations");
          if (invitation.requestId) {
            updatedStatusMessages.set(invitation.requestId, BulkInviteInvitationStatus.InviteError);
          }
          continue;
        }

        if (!programId) {
          console.error("Program ID is required to create invitations");
          if (invitation.requestId) {
            updatedStatusMessages.set(invitation.requestId, BulkInviteInvitationStatus.InviteError);
          }
          continue;
        }

        // Validate required fields before sending to lambda
        if (!invitation.firstName?.trim() || !invitation.lastName?.trim() || !invitation.email?.trim()) {
          console.error("Missing required fields for invitation:", {
            requestId: invitation.requestId,
            firstName: invitation.firstName,
            lastName: invitation.lastName,
            email: invitation.email
          });
          if (invitation.requestId) {
            updatedStatusMessages.set(invitation.requestId, BulkInviteInvitationStatus.InviteError);
          }
          continue;
        }

        if (!invitation.role || !Object.values(ProgramRole).includes(invitation.role as ProgramRole)) {
          console.error("Invalid or missing role for invitation:", {
            requestId: invitation.requestId,
            role: invitation.role
          });
          if (invitation.requestId) {
            updatedStatusMessages.set(invitation.requestId, BulkInviteInvitationStatus.InviteError);
          }
          continue;
        }

        // Prepare input for create-user lambda
        // The lambda handles: user creation, program membership creation, and email sending
        // All operations are sequential within the lambda (transaction-based)
        const createUserInput = {
          firstName: invitation.firstName.trim(),
          lastName: invitation.lastName.trim(),
          email: invitation.email.trim().toLowerCase(),
          adminId: currentUser.id,
          programId: programId,
          programRole: invitation.role,
          startDate: new Date().toISOString(),
          ...(invitation.npi && { npi: invitation.npi }),
          ...(invitation.pgy && { pgy: invitation.pgy }),
          ...(invitation.simplLearnId && invitation.simplLearnId.trim() && { simplLearnId: invitation.simplLearnId.trim() }),
          ...(invitation.jobTitle && invitation.jobTitle.trim() && { jobTitle: invitation.jobTitle.trim() }),
          ...(invitation.role === ProgramRole.Trainee && invitation.traineeType && {
            traineeType: invitation.traineeType
          })
        };

        // Log the input for debugging (remove sensitive data in production)
        console.log("Creating user with input:", {
          ...createUserInput,
          email: createUserInput.email // Log email for debugging
        });

        // Single call to create-user lambda (handles everything sequentially in a transaction)
        // We await this because we need to know if it succeeded before proceeding to next invitation
        const result = await createUserAPI(createUserInput);

        if (result && result.success && result.userId) {
          // All steps succeeded! The lambda has:
          // - Created the user
          // - Created the program membership
          // - Sent the invitation email
          // - Updated invitation status
          if (invitation?.requestId) {
            updatedStatusMessages.set(invitation.requestId, BulkInviteInvitationStatus.Sent);
            // Update state immediately so UI shows progress
            setStatusMessages(new Map(updatedStatusMessages));
          }
          continue; // Move to next invitation
        } else {
          // Lambda returned an error or null result - display the error message to the user
          const errorMessage: string = result?.error || "Error creating user, invitation, and/or program membership";
          console.error("Error creating user via lambda:", errorMessage);
          if (invitation.requestId) {
            updatedStatusMessages.set(invitation.requestId, errorMessage);
            // Update state immediately so UI shows error
            setStatusMessages(new Map(updatedStatusMessages));
          }
          continue; // Move to next invitation even on error
        }
      } catch (error) {
        console.error("error in processInvitations:", error);
        if (invitation && invitation.requestId) {
          updatedStatusMessages.set(invitation.requestId, BulkInviteInvitationStatus.NetworkError);
          // Update state immediately so UI shows error
          setStatusMessages(new Map(updatedStatusMessages));
        }
        continue;
      }
    }
    
    // Final state update with all status messages
    setStatusMessages(updatedStatusMessages);
  }
  } finally {
    // Clear loading state
    if (setIsProcessing) {
      setIsProcessing(false);
    }
  }
};

const bulkInviteFieldsToCheck: (keyof BulkInvitation)[] = [
  "firstName",
  "lastName",
  "email",
  "npi",
  "pgy",
  "role",
  "traineeType",
  "programId",
  "jobTitle"
];
type BulkInviteFieldsAreEqualProps = {
  field: keyof BulkInvitation;
  firstInvitation: BulkInvitation;
  secondInvitation: BulkInvitation;
};
const bulkInviteFieldsAreEqual = ({
  field,
  firstInvitation,
  secondInvitation
}: BulkInviteFieldsAreEqualProps) => {
  let firstValue = firstInvitation[field];
  let secondValue = secondInvitation[field];
  if (field === "npi" || field === "pgy") {
    firstValue = Number(firstValue || "");
    secondValue = Number(secondValue || "");
  }
  const areEqual = firstValue === secondValue;
  return areEqual;
};

type BulkInviteTableRowUpdateProps = {
  currentInvitations: BulkInvitation[];
  oldData?: BulkInvitation;
  newData: BulkInvitation;
  setInvitations: Dispatch<SetStateAction<BulkInvitation[] | undefined>>;
  statusMessages?: Map<string, BulkInviteInvitationStatus | string>;
  setStatusMessages?: Dispatch<SetStateAction<Map<string, BulkInviteInvitationStatus | string>>>;
};
export const bulkInviteTableRowUpdate = ({
  currentInvitations,
  oldData,
  newData,
  setInvitations,
  statusMessages,
  setStatusMessages
}: BulkInviteTableRowUpdateProps) => {
  const updatedInvitations: BulkInvitation[] = [
    ...currentInvitations
  ] as BulkInvitation[];
  if (oldData) {
    const targetRow = updatedInvitations.find(
      (invitation) => invitation.requestId === oldData.requestId
    );
    
    // Check if email or NPI changed (these are duplicate-related fields)
    const emailChanged = oldData.email?.toLowerCase().trim() !== newData.email?.toLowerCase().trim();
    const npiChanged = oldData.npi !== newData.npi;
    const duplicateFieldChanged = emailChanged || npiChanged;
    
    // Track rows that need revalidation due to duplicate field changes
    const rowsNeedingRevalidation: string[] = [];
    
    if (duplicateFieldChanged && statusMessages && setStatusMessages) {
      // Find all rows that shared the old email or NPI with the updated row
      const oldEmail = oldData.email?.toLowerCase().trim();
      const oldNpi = oldData.npi !== undefined && oldData.npi !== null ? String(oldData.npi) : null;
      
      updatedInvitations.forEach((invitation) => {
        // Skip the row being updated
        if (invitation.requestId === oldData.requestId) return;
        
        let shouldMarkForRevalidation = false;
        
        // Check if this row shared the old email (case-insensitive)
        if (emailChanged && oldEmail) {
          const invitationEmail = invitation.email?.toLowerCase().trim();
          if (invitationEmail === oldEmail) {
            // Check if this row had a duplicate error for email
            const hasEmailDuplicateError = invitation.errors?.some(
              (error) => error.field === "email" && error.error.toLowerCase().includes("duplicate")
            );
            if (hasEmailDuplicateError) {
              shouldMarkForRevalidation = true;
            }
          }
        }
        
        // Check if this row shared the old NPI
        if (npiChanged && oldNpi) {
          const invitationNpi = invitation.npi !== undefined && invitation.npi !== null 
            ? String(invitation.npi) 
            : null;
          if (invitationNpi === oldNpi) {
            // Check if this row had a duplicate error for NPI
            const hasNpiDuplicateError = invitation.errors?.some(
              (error) => error.field === "npi" && error.error.toLowerCase().includes("duplicate")
            );
            if (hasNpiDuplicateError) {
              shouldMarkForRevalidation = true;
            }
          }
        }
        
        if (shouldMarkForRevalidation && invitation.requestId) {
          rowsNeedingRevalidation.push(invitation.requestId);
        }
      });
    }
    
    for (const field of bulkInviteFieldsToCheck) {
      if (!newData || !oldData) continue;
      const areEqual = bulkInviteFieldsAreEqual({
        field,
        firstInvitation: oldData,
        secondInvitation: newData
      });
      if (!areEqual) {
        const errorIndex = newData.errors?.findIndex(
          (error) => error.field === field
        );
        if (typeof errorIndex === "number" && newData.errors) {
          const updatedErrors = [...newData.errors];
          updatedErrors?.splice(errorIndex, 1);
          newData.errors = updatedErrors;
        }
      }
    }
    if (targetRow) {
      const targetRowIndex = updatedInvitations.indexOf(targetRow);
      updatedInvitations[targetRowIndex] = newData as BulkInvitation;
      setInvitations(updatedInvitations);
      
      // Update status messages for rows that need revalidation
      if (rowsNeedingRevalidation.length > 0 && statusMessages && setStatusMessages) {
        const updatedStatusMessages = new Map(statusMessages);
        rowsNeedingRevalidation.forEach((requestId) => {
          updatedStatusMessages.set(requestId, BulkInviteInvitationStatus.AwaitingRevalidation);
        });
        setStatusMessages(updatedStatusMessages);
      }
    }
  }
};

type BulkInviteTableRowDeleteProps = {
  currentInvitations: BulkInvitation[];
  // I HATE using 'any', but we still use material-table for Bulk Invite and it's horribly typed.
  // We need to switch over to MUI DataGrid as soon as possible.
  targetRow: any;
  setInvitations: Dispatch<SetStateAction<BulkInvitation[] | undefined>>;
  statusMessages?: Map<string, BulkInviteInvitationStatus | string>;
  setStatusMessages?: Dispatch<SetStateAction<Map<string, BulkInviteInvitationStatus | string>>>;
  onDelete?: (
    updatedInvitations: BulkInvitation[],
    deletedInvitation?: BulkInvitation,
    options?: { skipValidation?: boolean }
  ) => void;
  setRowsAwaitingRevalidation?: Dispatch<SetStateAction<Set<string>>>;
  skipValidationOnDelete?: boolean;
};
export const bulkInviteTableRowDelete = ({
  currentInvitations,
  targetRow,
  setInvitations,
  statusMessages,
  setStatusMessages,
  onDelete,
  setRowsAwaitingRevalidation,
  skipValidationOnDelete
}: BulkInviteTableRowDeleteProps) => {
  const updatedInvitations = [...currentInvitations];
  let targetRowIndex = null;
  targetRowIndex = targetRow?.tableData?.index;

  // If the targetRowIndex isn't a number yet, try to find a matching row.
  if (typeof targetRowIndex !== "number") {
    targetRowIndex = currentInvitations.findIndex((invitation) => {
      const foundMatch = isEqual(invitation, targetRow);
      return foundMatch;
    });
  }

  // If the targetRowIndex STILL isn't a number, we have nothing to delete.
  if (typeof targetRowIndex === "number") {
    const deletedRow = currentInvitations[targetRowIndex];
    updatedInvitations.splice(targetRowIndex, 1);
    
    // Get the deleted row's email and NPI to identify which rows might have been duplicates
    const deletedEmail = deletedRow?.email?.toLowerCase().trim();
    const deletedNpi = deletedRow?.npi !== undefined && deletedRow?.npi !== null 
      ? String(deletedRow.npi) 
      : null;
    
    // Track which rows had duplicate errors removed so we can mark them as awaiting revalidation
    const rowsNeedingRevalidation: string[] = [];
    
    // Re-check duplicates in the remaining invitations to see which ones still have duplicates
    // Build maps of emails and NPIs in remaining invitations
    const emailMap = new Map<string, string[]>(); // lowercase email -> array of requestIds
    const npiMap = new Map<string, string[]>(); // npi (as string) -> array of requestIds
    
    updatedInvitations.forEach((invitation) => {
      const requestId = invitation.requestId || "";
      
      // Track email (case-insensitive)
      if (invitation.email) {
        const emailLower = invitation.email.toLowerCase().trim();
        if (emailLower) {
          const existing = emailMap.get(emailLower) || [];
          existing.push(requestId);
          emailMap.set(emailLower, existing);
        }
      }
      
      // Track NPI
      if (invitation.npi !== undefined && invitation.npi !== null) {
        const npiValue = String(invitation.npi);
        if (npiValue && npiValue !== "") {
          const existing = npiMap.get(npiValue) || [];
          existing.push(requestId);
          npiMap.set(npiValue, existing);
        }
      }
    });
    
    // Only clear duplicate errors from rows that:
    // 1. Share the same email or NPI as the deleted row, AND
    // 2. No longer have duplicates (count is now 1)
    const cleanedInvitations = updatedInvitations.map((invitation) => {
      const requestId = invitation.requestId || "";
      let shouldCheckForDuplicates = false;
      
      // Check if this row shares email or NPI with the deleted row
      if (deletedEmail) {
        const invitationEmail = invitation.email?.toLowerCase().trim();
        if (invitationEmail === deletedEmail) {
          shouldCheckForDuplicates = true;
        }
      }
      
      if (deletedNpi && !shouldCheckForDuplicates) {
        const invitationNpi = invitation.npi !== undefined && invitation.npi !== null 
          ? String(invitation.npi) 
          : null;
        if (invitationNpi === deletedNpi) {
          shouldCheckForDuplicates = true;
        }
      }
      
      // Only process if this row was potentially a duplicate of the deleted row
      if (shouldCheckForDuplicates && invitation.errors && invitation.errors.length > 0) {
        const updatedErrors: ValidateBulkUploadError[] = [];
        let hadDuplicateError = false;
        
        // Check each error to see if it's a duplicate error that should be removed
        invitation.errors.forEach((error) => {
          const isDuplicateError = error.error.toLowerCase().includes("duplicate");
          
          if (isDuplicateError) {
            hadDuplicateError = true;
            
            // Check if the duplicate still exists
            let stillHasDuplicate = false;
            
            if (error.field === "email" && invitation.email) {
              const emailLower = invitation.email.toLowerCase().trim();
              const emailRequestIds = emailMap.get(emailLower);
              // If count > 1, there are still duplicates
              stillHasDuplicate = (emailRequestIds?.length || 0) > 1;
            } else if (error.field === "npi" && invitation.npi !== undefined && invitation.npi !== null) {
              const npiValue = String(invitation.npi);
              const npiRequestIds = npiMap.get(npiValue);
              // If count > 1, there are still duplicates
              stillHasDuplicate = (npiRequestIds?.length || 0) > 1;
            }
            
            // Only keep the duplicate error if duplicates still exist
            if (stillHasDuplicate) {
              updatedErrors.push(error);
            }
          } else {
            // Keep non-duplicate errors
            updatedErrors.push(error);
          }
        });
        
        // If we removed duplicate errors, mark for revalidation
        if (hadDuplicateError && updatedErrors.length < invitation.errors.length) {
          if (requestId) {
            rowsNeedingRevalidation.push(requestId);
          }
          return {
            ...invitation,
            errors: updatedErrors.length > 0 ? updatedErrors : undefined
          };
        }
      }
      
      return invitation;
    });
    
    setInvitations(cleanedInvitations);
    
    // Update status messages for rows that had duplicate errors removed
    // Set them to "Awaiting Revalidation" status
    if (statusMessages && setStatusMessages && rowsNeedingRevalidation.length > 0) {
      const updatedStatusMessages = new Map(statusMessages);
      rowsNeedingRevalidation.forEach((requestId) => {
        updatedStatusMessages.set(requestId, BulkInviteInvitationStatus.AwaitingRevalidation);
      });
      setStatusMessages(updatedStatusMessages);
    }

    if (setRowsAwaitingRevalidation && rowsNeedingRevalidation.length > 0) {
      setRowsAwaitingRevalidation((prev) => {
        const updated = new Set(prev);
        rowsNeedingRevalidation.forEach((requestId) => {
          updated.add(requestId);
        });
        return updated;
      });
    }
    
    // Call onDelete callback if provided (e.g., to trigger revalidation)
    if (onDelete) {
      onDelete(cleanedInvitations, deletedRow, {
        skipValidation: skipValidationOnDelete
      });
    }
  }
};

type BulkInviteRowRenderReturn = {
  status: string;
  color: string;
};

export const bulkInviteRowRender = (
  row: BulkInvitation & {
    tableData?:
      | {
          editing: string;
        }
      | undefined;
  },
  currentInvitations: BulkInvitation[],
  validatedInvitations: BulkInvitation[],
  statusMessages: Map<string, string>
): BulkInviteRowRenderReturn => {
  const requestId = row.requestId;
  const unvalidatedInvitation = currentInvitations.find(
    (invitation) => invitation.requestId === requestId
  );
  const validatedInvitation = validatedInvitations.find(
    (invitation) => invitation.requestId === requestId
  );
  const needsRevalidation =
    unvalidatedInvitation && validatedInvitation
      ? invitationNeedsRevalidation(unvalidatedInvitation, validatedInvitation)
      : false;
  const status = needsRevalidation
    ? BulkInviteInvitationStatus.AwaitingRevalidation
    : (row.requestId && statusMessages.get(row.requestId)) ??
      BulkInviteInvitationStatus.ReadyToInvite;
  let color = "black";
  switch (status) {
    case BulkInviteInvitationStatus.AwaitingRevalidation:
      color = "orange";
      break;
    case BulkInviteInvitationStatus.ReadyToInvite:
      color = "secondary";
      break;
    case BulkInviteInvitationStatus.Sent:
      color = "success.main";
      break;
    default:
      color = "error";
      break;
  }

  return {
    status,
    color
  };
};

export const invitationNeedsRevalidation = (
  invitation: BulkInvitation,
  validatedInvitation: BulkInvitation | undefined
): boolean => {
  if (!validatedInvitation) return false;

  for (const field of bulkInviteFieldsToCheck) {
    if (
      invitation[field as keyof BulkInvitation] !==
      validatedInvitation[field as keyof BulkInvitation]
    ) {
      return true;
    }
  }
  return false;
};

export const bulkInviteRowHasError = (
  invitation: BulkInvitation,
  field: string,
  editableInvitations: BulkInvitation[],
  currentInvitations: BulkInvitation[]
): boolean => {
  const fieldName = field as keyof BulkInvitation;
  const index = editableInvitations.findIndex(
    (ei) => ei.email == invitation.email
  );
  const uneditedInvitation = currentInvitations[index];
  return (
    !!uneditedInvitation &&
    uneditedInvitation[fieldName] == invitation[fieldName] &&
    !!invitation.errors?.find((e) => e.field == field)
  );
};

export const bulkInviteGetErrorByField = (
  invitation: BulkInvitation,
  field: string
) => {
  return invitation.errors?.filter((e) => e.field == field) ?? [];
};

export const bulkInviteGetFormattedErrors = (
  invitation: BulkInvitation,
  field: string,
  defaultMessage: string,
  editableInvitations: BulkInvitation[],
  currentInvitations: BulkInvitation[]
) =>
  bulkInviteRowHasError(
    invitation,
    field,
    editableInvitations,
    currentInvitations
  )
    ? bulkInviteGetErrorByField(invitation, field)
        .map((e) => e.error)
        .join(", ")
    : defaultMessage;

export const bulkInviteRowContainsExistingUserError = (
  row: BulkInvitation,
  statusMessages: Map<string, string>
) => {
  const status = row.requestId ? statusMessages.get(row.requestId) : null;
  return status === BulkInviteInvitationStatus.ExistingUser;
};

export const createAndSetStatusMessageMap = (
  invitations: BulkInvitation[] | undefined,
  setStatusMessages: (value: SetStateAction<Map<string, string>>) => void,
  rowsAwaitingRevalidation?: Set<string>
) => {
  const statusMessagesMap = new Map<
    string,
    BulkInviteInvitationStatus | string
  >();
  invitations?.forEach((invitation) => {
    if (invitation.requestId) {
      if (rowsAwaitingRevalidation?.has(invitation.requestId)) {
        statusMessagesMap.set(
          invitation.requestId,
          BulkInviteInvitationStatus.AwaitingRevalidation
        );
        return;
      }
      const invitationErrors = invitation.errors;
      if (invitationErrors && invitationErrors.length > 0) {
        let errorMessage: BulkInviteInvitationStatus | string =
          BulkInviteInvitationStatus.MultipleErrors;
        if (invitationErrors.length === 1) {
          errorMessage = invitationErrors[0].error;
        }
        const existingUserErrors = [
          BulkInviteErrors.EMAIL_EXISTS,
          BulkInviteErrors.NPI_EXISTS
        ];
        const containsExistingUserError = invitationErrors.some((error) =>
          existingUserErrors.includes(error.error)
        );
        if (containsExistingUserError) {
          errorMessage = BulkInviteInvitationStatus.ExistingUser;
        }

        statusMessagesMap.set(invitation.requestId, errorMessage);
      } else {
        statusMessagesMap.set(
          invitation.requestId,
          BulkInviteInvitationStatus.ReadyToInvite
        );
      }
    }
  });
  setStatusMessages(statusMessagesMap);
};
